import { datadogLogs } from '@datadog/browser-logs'
import axios from 'axios'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { useQuestionnaireParams } from './useQuestionnaireParams'

interface RedirectUrlResponse {
  redirectUrl: string | null
}

// Upper bound on the resolver call so a hung backend can't leave the request
// pending indefinitely. Generous (30s) on purpose: the redirect is the valuable
// outcome (supplier attribution), so we'd rather wait out a transient backend
// slowdown than drop it — and the respondent sees the close-page screen (and can
// leave) while we wait, so a longer bound has no UX cost. On timeout we fall
// back to the close-page screen. Not env-configurable.
const RESOLVER_TIMEOUT_MS = 30000

/**
 * Resolves the supplier end-link for a custom-audience (ps=fd) respondent.
 *
 * The backend (fd-core-respondent) fully resolves the URL server-side from the
 * respondent's stored terminal status and returns
 * `{ redirectUrl: string | null }`, where `null` means no redirect is
 * configured for the survey (or the stored status is non-terminal/unmapped).
 *
 * Any failure (network error, unexpected shape, non-2xx, timeout) resolves to
 * `null` so the caller always falls back to the close-page screen and the end
 * screen is never blocked. Uses a single timeout-bounded request with NO retry
 * (like usePerformQualityCheck) so a slow/erroring backend cannot stretch the
 * respondent's pre-redirect wait beyond one timeout.
 */
const useGetRedirectUrl = () => {
  const mandatoryQueryParams = useQuestionnaireParams()

  const getRedirectUrl = async (): Promise<string | null> => {
    if (!mandatoryQueryParams) {
      // NOTE: should never get here — useQuestionnaireParams throws when the
      // mandatory params are missing.
      datadogLogs.logger.error(
        'Get redirect URL skipped: Missing mandatory query params'
      )
      return null
    }

    const { surveyId, respondentId } = mandatoryQueryParams

    const url = coreRespondentUrl(
      `/surveys/${surveyId}/respondents/supplier/${respondentId}/redirect`
    )

    try {
      const response = await axios.get<RedirectUrlResponse>(url, {
        timeout: RESOLVER_TIMEOUT_MS
      })
      const redirectUrl = response?.data?.redirectUrl ?? null

      datadogLogs.logger.info('Resolved custom-audience redirect URL', {
        surveyId,
        respondentId,
        hasRedirectUrl: redirectUrl !== null
      })

      return redirectUrl
    } catch (error) {
      datadogLogs.logger.error(
        'Failed to resolve custom-audience redirect URL',
        { error, surveyId, respondentId }
      )
      return null
    }
  }

  return getRedirectUrl
}

export default useGetRedirectUrl
